/**
 * Vendor File Upload API — S3 Multipart Upload via Presigned URLs
 *
 * This service handles large file uploads (30-40GB+) directly from the browser
 * to S3 using multipart upload with presigned URLs. The backend only:
 *   1. Creates the multipart upload session (returns uploadId + S3 key)
 *   2. Generates presigned URLs for each part
 *   3. Completes or aborts the multipart upload
 *
 * The actual file data never touches the backend — it goes browser → S3 directly.
 *
 * Authentication: The vendor link contains a token (?token=xxx). The backend
 * validates this token before issuing presigned URLs.
 *
 * ----------------------------------------------------------------------------
 * BACKEND ENDPOINTS REQUIRED (to be implemented on the DigitalOcean app):
 *
 * POST /files/vendor-upload/init
 *   Body: { token, fileName, contentType, folder }
 *   Returns: { uploadId, key, bucket }
 *
 * GET /files/vendor-upload/presign
 *   Query: ?token=...&uploadId=...&key=...&partNumber=...
 *   Returns: { url }
 *
 * POST /files/vendor-upload/complete
 *   Body: { token, uploadId, key, parts: [{ PartNumber, ETag }] }
 *   Returns: { location, bucket, key, cdnUrl }
 *
 * POST /files/vendor-upload/abort
 *   Body: { token, uploadId, key }
 *   Returns: { success: true }
 * ----------------------------------------------------------------------------
 */

const API_BASE_URL =
  process.env.REACT_APP_GOTTEA_API_BASE_URL ||
  'https://gottea-search-5gtrn.ondigitalocean.app';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Calculate an appropriate part size for the given file.
 * Targets ~200 parts, clamped to S3 limits (5MB min, 5GB max).
 */
export function calculatePartSize(fileSize) {
  const MIN_PART_SIZE = 5 * 1024 * 1024; // 5 MB (S3 minimum)
  const MAX_PART_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB (S3 maximum)
  const MAX_PARTS = 10000;
  const TARGET_PARTS = 200;

  let partSize = Math.ceil(fileSize / TARGET_PARTS);
  // Round up to nearest 5MB
  partSize = Math.ceil(partSize / MIN_PART_SIZE) * MIN_PART_SIZE;

  // Ensure we don't exceed max parts
  if (Math.ceil(fileSize / partSize) > MAX_PARTS) {
    partSize = Math.ceil(fileSize / MAX_PARTS);
    partSize = Math.ceil(partSize / MIN_PART_SIZE) * MIN_PART_SIZE;
  }

  return Math.max(MIN_PART_SIZE, Math.min(MAX_PART_SIZE, partSize));
}

/** Split a File into an array of part descriptors (offset, size, number). */
export function getParts(fileSize, partSize) {
  const parts = [];
  let offset = 0;
  let partNumber = 1;
  while (offset < fileSize) {
    const size = Math.min(partSize, fileSize - offset);
    parts.push({ number: partNumber, offset, size });
    offset += size;
    partNumber++;
  }
  return parts;
}

/** Format bytes into a human-readable string. */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/** Format seconds into a human-readable duration. */
export function formatDuration(seconds) {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h ${remMins}m`;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// API calls (all go through the backend, which talks to S3 / SDK)
// ---------------------------------------------------------------------------

/** Initialize a multipart upload session on the backend. */
export async function initMultipartUpload({ token, fileName, contentType, folder }) {
  const res = await fetch(`${API_BASE_URL}/files/vendor-upload/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, fileName, contentType, folder }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to initialize upload (${res.status})`);
  }
  return res.json(); // { uploadId, key, bucket }
}

/** Get a presigned URL for uploading a single part. */
export async function getPresignedUrl({ token, uploadId, key, partNumber }) {
  const params = new URLSearchParams({
    token,
    uploadId,
    key,
    partNumber: String(partNumber),
  });
  const res = await fetch(
    `${API_BASE_URL}/files/vendor-upload/presign?${params.toString()}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to get presigned URL (${res.status})`);
  }
  const data = await res.json();
  return data.url;
}

/** Upload a single part to S3 via presigned URL (PUT). Returns the ETag. */
export async function uploadPart({ file, offset, size, presignedUrl, signal }) {
  const chunk = file.slice(offset, offset + size);
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    body: chunk,
    signal,
  });
  if (!res.ok) {
    throw new Error(`Part upload failed (${res.status})`);
  }
  // S3 returns the ETag in the response header
  const etag = res.headers.get('ETag') || res.headers.get('etag');
  if (!etag) {
    throw new Error('Missing ETag in upload response');
  }
  // S3 wraps ETag in double quotes — strip them
  return etag.replace(/^"|"$/g, '');
}

/** Complete the multipart upload on the backend. */
export async function completeMultipartUpload({ token, uploadId, key, parts }) {
  const res = await fetch(`${API_BASE_URL}/files/vendor-upload/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      uploadId,
      key,
      parts: parts
        .sort((a, b) => a.PartNumber - b.PartNumber)
        .map((p) => ({ PartNumber: p.PartNumber, ETag: p.ETag })),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to complete upload (${res.status})`);
  }
  return res.json(); // { location, bucket, key, cdnUrl }
}

/** Abort the multipart upload (cleans up partial parts on S3). */
export async function abortMultipartUpload({ token, uploadId, key }) {
  try {
    await fetch(`${API_BASE_URL}/files/vendor-upload/abort`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, uploadId, key }),
    });
  } catch (e) {
    // Best-effort — don't throw if abort fails
    console.warn('Abort failed:', e);
  }
}

// ---------------------------------------------------------------------------
// High-level orchestrator: upload a single file with multipart + concurrency
// ---------------------------------------------------------------------------

/**
 * Upload a large file to S3 using multipart upload with presigned URLs.
 *
 * @param {Object}   opts
 * @param {File}     opts.file         — the file to upload
 * @param {string}   opts.token        — vendor access token
 * @param {string}   [opts.folder]     — S3 folder prefix (default: 'vendor-uploads')
 * @param {number}   [opts.concurrency]— parallel part uploads (default: 3)
 * @param {Function} [opts.onProgress] — (uploadedBytes, totalBytes, speedMBps) => void
 * @param {AbortSignal} [opts.signal]  — abort signal for cancellation
 * @returns {Promise<{ location, bucket, key, cdnUrl }>}
 */
export async function uploadLargeFile({
  file,
  token,
  folder = 'vendor-uploads',
  concurrency = 3,
  onProgress,
  signal,
}) {
  const partSize = calculatePartSize(file.size);
  const parts = getParts(file.size, partSize);
  const uploadedParts = []; // { PartNumber, ETag }
  let uploadedBytes = 0;
  const startTime = Date.now();

  // 1. Initialize multipart upload
  const { uploadId, key } = await initMultipartUpload({
    token,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    folder,
  });

  const reportProgress = () => {
    const elapsedSec = (Date.now() - startTime) / 1000;
    const speedMBps = elapsedSec > 0 ? uploadedBytes / 1024 / 1024 / elapsedSec : 0;
    onProgress?.(uploadedBytes, file.size, speedMBps);
  };

  // 2. Upload parts with limited concurrency
  let partIndex = 0;

  async function worker() {
    while (partIndex < parts.length) {
      if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');

      const part = parts[partIndex++];
      // Get presigned URL for this part (with retry)
      let presignedUrl;
      let retries = 0;
      while (retries < 3) {
        try {
          presignedUrl = await getPresignedUrl({
            token,
            uploadId,
            key,
            partNumber: part.number,
          });
          break;
        } catch (e) {
          retries++;
          if (retries >= 3) throw e;
          await delay(1000 * retries);
        }
      }

      // Upload the part (with retry)
      retries = 0;
      while (retries < 3) {
        if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
        try {
          const etag = await uploadPart({
            file,
            offset: part.offset,
            size: part.size,
            presignedUrl,
            signal,
          });
          uploadedParts.push({ PartNumber: part.number, ETag: etag });
          uploadedBytes += part.size;
          reportProgress();
          break;
        } catch (e) {
          if (e.name === 'AbortError') throw e;
          retries++;
          if (retries >= 3) throw e;
          await delay(1000 * retries);
        }
      }
    }
  }

  // Launch concurrent workers
  const workers = [];
  for (let i = 0; i < Math.min(concurrency, parts.length); i++) {
    workers.push(worker());
  }

  try {
    await Promise.all(workers);
  } catch (e) {
    // Abort the multipart upload on failure
    await abortMultipartUpload({ token, uploadId, key });
    throw e;
  }

  // 3. Complete the multipart upload
  const result = await completeMultipartUpload({
    token,
    uploadId,
    key,
    parts: uploadedParts,
  });

  return result;
}
